import axios from 'axios';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';

export interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'provider' | 'admin';
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
  isActive: boolean;
  externalId?: string;
  lastEhrSync?: Date;
}

export interface ProviderFilters {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  specialty?: string;
  isActive?: boolean;
}

export interface ProviderResult {
  providers: Provider[];
  total: number;
  page: number;
  limit: number;
}

export interface SimpleProvider {
  id: string;
  name: string;
  email: string;
  role: 'provider' | 'admin';
  isSelf: boolean;
}

export interface ProvidersResponse {
  providers: SimpleProvider[];
  canAssignToOthers: boolean;
}

class ProviderService {
  private baseUrl = `${API_BASE_URL}/v1/providers`;

  // Get all providers with filtering and pagination
  async getProviders(filters?: ProviderFilters): Promise<ProviderResult> {
    try {
      const response = await axios.get<{ success: boolean; data: ProviderResult }>(
        this.baseUrl,
        {
          headers: getAuthHeader(),
          params: filters
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch providers:', error);
      throw error;
    }
  }

  // Get a specific provider
  async getProvider(providerId: string): Promise<Provider> {
    try {
      const response = await axios.get<{ success: boolean; data: Provider }>(
        `${this.baseUrl}/${providerId}`,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch provider:', error);
      throw error;
    }
  }

  // Create a new provider (standalone mode only)
  async createProvider(providerData: Partial<Provider>): Promise<Provider> {
    try {
      const response = await axios.post<{ success: boolean; data: Provider }>(
        this.baseUrl,
        providerData,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to create provider:', error);
      if (error.response?.data?.error?.includes('connected mode')) {
        throw new Error('Cannot create providers in connected mode. Providers are synced from EHR.');
      }
      throw error;
    }
  }

  // Update a provider
  async updateProvider(providerId: string, providerData: Partial<Provider>): Promise<Provider> {
    try {
      const response = await axios.put<{ success: boolean; data: Provider }>(
        `${this.baseUrl}/${providerId}`,
        providerData,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to update provider:', error);
      if (error.response?.data?.error?.includes('connected mode')) {
        throw new Error('Limited provider updates in connected mode. Only certain fields can be modified.');
      }
      throw error;
    }
  }

  // Delete a provider (standalone mode only)
  async deleteProvider(providerId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.baseUrl}/${providerId}`,
        { headers: getAuthHeader() }
      );
    } catch (error: any) {
      console.error('Failed to delete provider:', error);
      if (error.response?.data?.error?.includes('connected mode')) {
        throw new Error('Cannot delete providers in connected mode. Providers are managed by EHR.');
      }
      throw error;
    }
  }

  // Sync providers from EHR (connected mode only)
  async syncProviders(): Promise<void> {
    try {
      await axios.post(
        `${this.baseUrl}/sync`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error: any) {
      console.error('Failed to sync providers:', error);
      if (error.response?.data?.error?.includes('standalone mode')) {
        throw new Error('Provider sync is only available in connected mode.');
      }
      throw error;
    }
  }

  // Get simple provider list for appointment assignment
  async getSimpleProviders(): Promise<ProvidersResponse> {
    try {
      const response = await axios.get<{ success: boolean; data: ProvidersResponse }>(
        `${API_BASE_URL}/v1/appointments/providers`,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch simple providers:', error);
      throw error;
    }
  }

  // Get all unique departments
  async getDepartments(): Promise<string[]> {
    try {
      const response = await axios.get<{ success: boolean; data: string[] }>(
        `${this.baseUrl}/metadata/departments`,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch departments:', error);
      throw error;
    }
  }

  // Get all unique specialties
  async getSpecialties(): Promise<string[]> {
    try {
      const response = await axios.get<{ success: boolean; data: string[] }>(
        `${this.baseUrl}/metadata/specialties`,
        { headers: getAuthHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch specialties:', error);
      throw error;
    }
  }

  // Helper method to format provider name
  formatProviderName(provider: Provider | SimpleProvider): string {
    if ('firstName' in provider && 'lastName' in provider) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    if ('name' in provider) {
      return provider.name;
    }
    return 'Unknown Provider';
  }

  // Helper method to check if provider is from EHR
  isEhrProvider(provider: Provider): boolean {
    return !!provider.externalId;
  }

  // Helper method to get provider display info
  getProviderDisplayInfo(provider: Provider): string {
    const parts = [this.formatProviderName(provider)];
    
    if (provider.profile?.title) {
      parts.push(provider.profile.title);
    }
    
    if (provider.profile?.department) {
      parts.push(`(${provider.profile.department})`);
    }
    
    return parts.join(' ');
  }
}

export const providerService = new ProviderService();